import React, { useState, useImperativeHandle, forwardRef, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { toast } from 'react-toastify';
import { useDataContext } from '../../context/data.js';
import { languageToPhoneCountry } from '../../utils/language';
import translations from '../../constants/translations.json';

const GuestInformation = forwardRef(
  (
    {
      guestInfo,
      setGuestInfo,
      phoneNumber,
      onPhoneNumberChange,
      isValidPhone,
      setIsValidPhone,
      restaurant,
      fromHeyCharity,
      tableNumber = '1',
      orderType,
      isParkTable = false,
    },
    ref,
  ) => {
    const { language } = useDataContext();

    // Form validation states
    const [nameError, setNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [formTouched, setFormTouched] = useState(false);

    // Refs for scrolling to error fields
    const nameRef = useRef(null);
    const phoneRef = useRef(null);
    const emailRef = useRef(null);

    // Validate name
    const validateName = (value) => {
      if (!value || value.trim() === '') {
        setNameError(true);
        toast.error(translations[language]?.guest_information_name_error);
        return false;
      }
      setNameError(false);
      return true;
    };

    // Validate email
    const validateEmail = (value, isOnNext = false) => {
      if (!value || value.trim() === '') {
        if (fromHeyCharity) {
          setEmailError(true);
          return false;
        }

        // Wolt delivery orders need an email — only complain on Next, so the
        // field isn't flagged while the guest is still filling the form.
        if (orderType === 'delivery') {
          if (isOnNext) {
            setEmailError(true);
            toast.error(translations[language]?.guest_information_email_error);
            return false;
          }

          return true;
        }

        setEmailError(false);
        return true;
      }

      // Basic email validation
      const emailRegex = /^[a-zA-Z0-9._]+@[a-zA-Z0-9._]+\.[a-zA-Z]{2,}$/;
      const isValid = emailRegex.test(value);
      setEmailError(!isValid);
      if (!isValid) {
        toast.error(translations[language]?.guest_information_email_error);
      }

      return isValid;
    };

    // Validate phone number (using existing logic from OrderInformation)
    const validatePhone = (value) => {
      // Basic phone validation - at least 8 digits
      const isValid = value && value.replace(/\D/g, '').length >= 8;
      setIsValidPhone?.(isValid);

      if (!isValid) {
        toast.error(translations[language]?.guest_information_phone_error);
      }
      return isValid;
    };

    // Validate all required fields
    const validateForm = (isOnNext = false) => {
      setFormTouched(true);
      let isValid = true;
      let firstErrorRef = null;

      // Validate name
      const isNameValid = validateName(guestInfo.name);
      if (!isNameValid && !firstErrorRef) {
        firstErrorRef = nameRef;
      }
      isValid = isValid && isNameValid;

      // Validate phone
      const isPhoneValid = validatePhone(phoneNumber);
      if (!isPhoneValid && !firstErrorRef) {
        firstErrorRef = phoneRef;
      }
      isValid = isValid && isPhoneValid;

      // Validate email
      const isEmailValid = validateEmail(guestInfo.email, isOnNext);
      if (!isEmailValid && !firstErrorRef) {
        firstErrorRef = emailRef;
      }
      isValid = isValid && isEmailValid;

      // Scroll to first error field
      if (firstErrorRef?.current) {
        firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return isValid;
    };

    // Expose the validateForm function to parent component
    useImperativeHandle(ref, () => ({
      validateForm,
    }));

    // Custom handlers with validation
    const handleNameChange = (value) => {
      setGuestInfo({ ...guestInfo, name: value });
      if (formTouched) {
        validateName(value);
      }
    };

    const handleEmailChange = (value) => {
      setGuestInfo({ ...guestInfo, email: value });
      if (formTouched) {
        validateEmail(value);
      }
    };

    // Error message style
    const errorMsgStyle = 'text-red-500 text-sm mt-1';
    const errorBorderStyle = '!border-red-500';

    return (
      <div className="order-information-container space-y-2">
        <div className="!px-3 !py-6 lg:!px-4 lg:!py-7 border-b border-[#f5f5f5]">
          <h2 className="text-base sm:text-lg md:text-xl font-bold">
            {translations[language]?.guest_information_title}
          </h2>
        </div>

        {/* Name or nickname */}
        <div
          ref={nameRef}
          className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row"
        >
          <div className="flex-1 mb-2 sm:!mb-0">
            <label className="block text-sm lg:text-base font-bold">
              {fromHeyCharity
                ? 'Fornavn og Efternavn'
                : translations[language]?.guest_information_name_label}
              *
            </label>
            <p className="text-sm text-[#707070]">
              {fromHeyCharity
                ? 'Til gæstelisten, konkurrencer mm.'
                : translations[language]?.guest_information_name_description}
            </p>
          </div>
          <div className="flex-1">
            <input
              type="text"
              placeholder={`${translations[language]?.guest_information_name_placeholder}...`}
              value={guestInfo.name || ''}
              onChange={(e) => handleNameChange(e.target.value)}
              className={`text-sm w-full px-4 py-[14px] border rounded-full appearance-none ${
                nameError ? errorBorderStyle : 'border-[#f4f4f4]'
              }`}
            />
            {nameError && (
              <p className={errorMsgStyle}>
                {translations[language]?.guest_information_name_error}
              </p>
            )}
          </div>
        </div>

        {/* Phone number */}
        <div
          ref={phoneRef}
          className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row"
        >
          <div className="flex-1 mb-2 sm:!mb-0">
            <label className="block text-sm lg:text-base font-bold">
              {translations[language]?.guest_information_phone_label}*
            </label>
            <p className="text-sm text-[#707070]">
              {fromHeyCharity
                ? 'Gratis SMS notifikation med ordrebekræftelse på din billet'
                : translations[language]?.guest_information_phone_description}
            </p>
          </div>
          <div className="flex-1 flex flex-col items-start">
            <PhoneInput
              country={languageToPhoneCountry(
                language,
                restaurant?.currency?.code === 'NOK' ? 'no' : 'dk',
              )}
              value={phoneNumber}
              onChange={onPhoneNumberChange}
              tabIndex={'4'}
              className={`phoneinput ${!isValidPhone ? 'phone-error' : ''}`}
              containerClass={!isValidPhone ? 'phone-container-error' : ''}
              inputClass={!isValidPhone ? 'phone-input-error' : ''}
            />
            {!isValidPhone && (
              <p className={errorMsgStyle}>
                {translations[language]?.guest_information_phone_error}
              </p>
            )}
          </div>
        </div>

        {/* Email */}
        <div ref={emailRef} className="!px-3 !py-4 lg:!px-4 lg:!py-8 flex flex-col sm:flex-row">
          <div className="flex-1 mb-2 sm:!mb-0">
            <label className="block text-sm lg:text-base font-bold">
              {translations[language]?.guest_information_email_label}
              {fromHeyCharity && '*'}
            </label>
            <p className="text-sm text-[#707070]">
              {fromHeyCharity
                ? 'Til din billet og dine lodder mm.'
                : translations[language]?.guest_information_email_description}
            </p>
          </div>
          <div className="flex-1">
            <input
              type="email"
              placeholder={`${translations[language]?.guest_information_email_placeholder}${
                fromHeyCharity || orderType === 'delivery'
                  ? ''
                  : ` (${translations[language]?.optional})`
              }...`}
              value={guestInfo.email || ''}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={`text-sm w-full px-4 py-[14px] border rounded-full appearance-none ${
                emailError ? errorBorderStyle : 'border-[#f4f4f4]'
              }`}
            />
            {emailError && (
              <p className={errorMsgStyle}>
                {translations[language]?.guest_information_email_error}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default GuestInformation;
